<template>
    <v-container fluid class="pa-4">
        <v-row>
            <!-- Sidebar: Plantilles -->
            <v-col cols="12" md="3">
                <v-card variant="outlined">
                    <v-toolbar density="comfortable" color="transparent">
                        <v-text-field
                            v-model="search"
                            prepend-inner-icon="mdi-magnify"
                            density="compact"
                            variant="outlined"
                            hide-details
                            placeholder="Cercar plantilla…"
                        />
                        <v-btn class="ml-2" color="primary" variant="flat" @click="newTemplate" icon="mdi-plus" />
                    </v-toolbar>

                    <v-divider />

                    <v-list lines="two" :disabled="loading.templates">
                        <v-list-item
                            v-for="tpl in filteredTemplates"
                            :key="tpl.slug"
                            :title="tpl.slug"
                            :subtitle="tpl.name"
                            :active="selected && selected.slug === tpl.slug"
                            @click="selectTemplate(tpl)"
                        >
                            <template #append>
                                <v-chip
                                    :color="tpl.is_active ? 'success' : 'grey'"
                                    size="x-small"
                                    variant="flat"
                                >{{ tpl.is_active ? 'activa' : 'inactiva' }}</v-chip>
                            </template>
                        </v-list-item>
                        <v-list-item v-if="loading.templates" title="Carregant plantilles…" />
                        <v-list-item v-else-if="!templates.length" title="Sense plantilles" subtitle="Crea'n una amb el botó +" />
                    </v-list>
                </v-card>
            </v-col>

            <!-- Editor: Plantilla / Variables -->
            <v-col cols="12" md="9">
                <v-card v-if="selected" variant="outlined">
                    <v-card-title class="d-flex align-center">
                        <span class="text-h6">Mailing</span>
                        <v-spacer />
                        <v-tabs v-model="tab" color="primary" density="comfortable">
                            <v-tab value="template" prepend-icon="mdi-file-document-edit-outline">Plantilla</v-tab>
                            <v-tab value="variables" prepend-icon="mdi-code-braces-box" @click="switchToVariables">Variables</v-tab>
                        </v-tabs>
                    </v-card-title>

                    <v-divider />

                    <v-window v-model="tab">
                        <!-- PESTANYA: PLANTILLA -->
                        <v-window-item value="template">
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="selected.slug" label="Slug" variant="outlined" />
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="selected.name" label="Nom" variant="outlined" />
                                    </v-col>

                                    <v-col cols="12" md="8">
                                        <v-text-field
                                            ref="subjectRef"
                                            v-model="selected.subject"
                                            label="Subjecte"
                                            variant="outlined"
                                            @focus="lastFocus = 'subject'"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field v-model="selected.locale" label="Idioma" variant="outlined" />
                                    </v-col>

                                    <v-col cols="12">
                                        <v-text-field v-model="selected.description" label="Descripció" variant="outlined" />
                                    </v-col>

                                    <v-col cols="12" md="3">
                                        <v-switch v-model="selected.is_active" color="primary" label="Activa" />
                                    </v-col>

                                    <v-col cols="12">
                                        <v-textarea
                                            ref="bodyRef"
                                            v-model="selected.body_blade"
                                            rows="14"
                                            auto-grow
                                            label="Cos (HTML)"
                                            variant="outlined"
                                            spellcheck="false"
                                            @focus="lastFocus = 'body'"
                                        />
                                    </v-col>
                                </v-row>
                            </v-card-text>

                            <!-- Barra d’inserció de variables -->
                            <v-card-text class="pt-0">
                                <v-card variant="tonal">
                                    <v-card-text class="d-flex align-center flex-wrap ga-3">
                                        <div class="text-medium-emphasis mr-2">Variables ràpides:</div>

                                        <!-- Selector de mode -->
                                        <v-segmented-button v-model="insertMode" class="mr-4" density="comfortable">
                                            <v-btn value="safe" prepend-icon="mdi-shield-check-outline" title="Escapat (recomanat)">Segur</v-btn>
                                            <v-btn value="raw" prepend-icon="mdi-code-tags" title="Sense escape (HTML)">HTML</v-btn>
                                            <v-btn value="block" prepend-icon="mdi-code-braces" title="Bloc condicional">@if</v-btn>
                                        </v-segmented-button>

                                        <!-- Xips de variables -->
                                        <template v-if="vars.length">
                                            <div class="text-medium-emphasis mr-2">Inserir a:</div>
                                            <v-chip size="small" variant="tonal" class="mr-2" :color="lastFocus==='subject' ? 'primary' : ''">
                                                {{ lastFocus === 'subject' ? 'Subjecte (actiu)' : 'Cos (actiu)' }}
                                            </v-chip>

                                            <div class="d-flex align-center flex-wrap ga-2">
                                                <v-chip
                                                    v-for="v in vars"
                                                    :key="v.key"
                                                    variant="flat"
                                                    color="primary"
                                                    size="small"
                                                    class="cursor-pointer"
                                                    @click="insertVar(v.key, 'auto')"
                                                    :title="v.description || v.label || v.key"
                                                >
                                                    {{ v.key }}
                                                </v-chip>
                                            </div>

                                            <v-spacer />

                                            <!-- Botons explícits -->
                                            <v-btn size="small" variant="text" prepend-icon="mdi-format-letter-case"
                                                   @click="insertVar(vars[0]?.key, 'subject')" :disabled="!vars.length">
                                                Al subjecte
                                            </v-btn>
                                            <v-btn size="small" variant="text" prepend-icon="mdi-text"
                                                   @click="insertVar(vars[0]?.key, 'body')" :disabled="!vars.length">
                                                Al cos
                                            </v-btn>
                                        </template>

                                        <template v-else>
                                            <div class="text-medium-emphasis">No hi ha variables definides (pestanya “Variables”).</div>
                                        </template>
                                    </v-card-text>
                                </v-card>
                            </v-card-text>

                            <v-card-actions class="px-4 pb-4">
                                <v-btn color="primary" :loading="saving" @click="saveTemplate" prepend-icon="mdi-content-save">
                                    Desar
                                </v-btn>
                                <v-spacer />
                                <v-text-field
                                    v-model="testTo"
                                    class="mr-2"
                                    style="max-width: 320px"
                                    label="Enviar prova a"
                                    variant="outlined"
                                    density="comfortable"
                                />
                                <v-btn
                                    color="secondary"
                                    :disabled="!selected.slug || !testTo"
                                    :loading="sending"
                                    @click="sendTest"
                                    prepend-icon="mdi-email-send-outline"
                                >
                                    Enviar prova
                                </v-btn>
                            </v-card-actions>
                        </v-window-item>

                        <!-- PESTANYA: VARIABLES -->
                        <v-window-item value="variables">
                            <v-card-text>
                                <v-row>
                                    <!-- Definició de variables -->
                                    <v-col cols="12" md="7">
                                        <div class="d-flex align-center mb-3">
                                            <span class="text-subtitle-1 font-weight-medium">Variables de la plantilla</span>
                                            <v-spacer />
                                            <v-btn class="mr-2" size="small" variant="tonal" @click="addVarRow" prepend-icon="mdi-plus">Afegir</v-btn>
                                            <v-btn size="small" color="primary" :loading="savingVars" @click="saveVariables" prepend-icon="mdi-content-save">
                                                Desar
                                            </v-btn>
                                        </div>

                                        <v-table density="comfortable">
                                            <thead>
                                            <tr>
                                                <th style="width:70px">#</th>
                                                <th style="min-width:170px">Clau</th>
                                                <th>Etiqueta</th>
                                                <th style="width:140px">Tipus</th>
                                                <th style="width:70px">Req</th>
                                                <th style="min-width:160px">Regles</th>
                                                <th style="min-width:160px">Default</th>
                                                <th>Descripció</th>
                                                <th style="width:80px"></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(v, i) in vars" :key="i">
                                                <td class="text-no-wrap">
                                                    <v-btn
                                                        size="x-small"
                                                        icon="mdi-arrow-up"
                                                        variant="text"
                                                        :disabled="i===0"
                                                        @click="moveVar(i,-1)"
                                                    />
                                                    <v-btn
                                                        size="x-small"
                                                        icon="mdi-arrow-down"
                                                        variant="text"
                                                        :disabled="i===vars.length-1"
                                                        @click="moveVar(i,1)"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field v-model="v.key" variant="outlined" density="compact" hide-details placeholder="user.name" />
                                                </td>
                                                <td>
                                                    <v-text-field v-model="v.label" variant="outlined" density="compact" hide-details placeholder="Nom d’usuari" />
                                                </td>
                                                <td>
                                                    <v-select
                                                        v-model="v.type"
                                                        :items="typeOptions"
                                                        variant="outlined"
                                                        density="compact"
                                                        hide-details
                                                    />
                                                </td>
                                                <td class="text-center">
                                                    <v-switch v-model="v.required" color="primary" hide-details inset density="compact" />
                                                </td>
                                                <td>
                                                    <v-text-field v-model="v.rules" variant="outlined" density="compact" hide-details placeholder="string|min:2, url, email…" />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-if="!usesJsonEditor(v.type)"
                                                        v-model="v.default"
                                                        variant="outlined"
                                                        density="compact"
                                                        hide-details
                                                        placeholder="valor per defecte"
                                                    />
                                                    <v-textarea
                                                        v-else
                                                        v-model="v.defaultJson"
                                                        rows="2"
                                                        auto-grow
                                                        variant="outlined"
                                                        density="compact"
                                                        hide-details
                                                        placeholder='JSON (p.ex. {"a":1})'
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field v-model="v.description" variant="outlined" density="compact" hide-details placeholder="Ajuda/notes" />
                                                </td>
                                                <td class="text-no-wrap">
                                                    <v-btn size="x-small" icon="mdi-content-copy" variant="text" @click="dupVar(i)" />
                                                    <v-btn size="x-small" icon="mdi-delete-outline" variant="text" @click="delVar(i)" />
                                                </td>
                                            </tr>

                                            <tr v-if="!vars.length && !loading.vars">
                                                <td colspan="9" class="text-medium-emphasis">Sense variables. Afegeix-ne amb “Afegir”.</td>
                                            </tr>
                                            <tr v-if="loading.vars">
                                                <td colspan="9" class="text-medium-emphasis">Carregant variables…</td>
                                            </tr>
                                            </tbody>
                                        </v-table>
                                    </v-col>

                                    <!-- JSON + Previsualització -->
                                    <v-col cols="12" md="5">
                                        <div class="d-flex align-center mb-3">
                                            <span class="text-subtitle-1 font-weight-medium">Payload i previsualització</span>
                                            <v-spacer />
                                            <v-btn size="small" class="mr-2" variant="tonal" @click="fillJsonDefaults" prepend-icon="mdi-auto-fix">Omplir defaults</v-btn>
                                            <v-btn size="small" variant="tonal" @click="copyPayload" prepend-icon="mdi-content-copy">Copiar JSON</v-btn>
                                        </div>

                                        <v-textarea
                                            v-model="payloadText"
                                            rows="10"
                                            auto-grow
                                            variant="outlined"
                                            label='JSON clau → valor (admet "user.name")'
                                            spellcheck="false"
                                            placeholder='{"user.name":"Maria","loginUrl":"https://…"}'
                                        />

                                        <div class="d-flex mt-2">
                                            <v-btn color="primary" :loading="rendering" @click="doRender" prepend-icon="mdi-eye-outline">
                                                Previsualitzar
                                            </v-btn>
                                        </div>

                                        <div v-if="render.subject || render.body" class="mt-4">
                                            <v-card variant="flat" class="mb-3">
                                                <v-card-title class="text-subtitle-2">Subjecte</v-card-title>
                                                <v-card-text>{{ render.subject }}</v-card-text>
                                            </v-card>

                                            <v-card variant="flat">
                                                <v-card-title class="text-subtitle-2">Cos</v-card-title>
                                                <v-card-text>
                                                    <div v-html="render.body"></div>
                                                </v-card-text>
                                            </v-card>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-window-item>
                    </v-window>
                </v-card>

                <v-card v-else variant="outlined">
                    <v-card-text class="text-medium-emphasis">Selecciona o crea una plantilla per començar.</v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Historial d'enviaments -->
        <v-row>
            <v-col cols="12">
                <v-card variant="outlined">
                    <v-card-title class="text-h6">Historial d’enviaments</v-card-title>
                    <v-divider />
                    <v-data-table
                        :headers="historyHeaders"
                        :items="messages.data || []"
                        :items-per-page="25"
                        density="comfortable"
                        class="text-body-2"
                    >
                        <template #item.created_at="{ item }">
                            {{ formatDate(item.created_at) }}
                        </template>
                        <template #item.status="{ item }">
                            <v-chip
                                :color="chipColor(item.status)"
                                size="small"
                                variant="tonal"
                            >{{ item.status }}</v-chip>
                        </template>
                        <template #item.subject="{ item }">
                            <span class="text-truncate d-inline-block" style="max-width: 420px">{{ item.subject }}</span>
                        </template>
                        <template #no-data>
                            <div class="pa-4 text-medium-emphasis">Sense registres.</div>
                        </template>
                    </v-data-table>

                    <v-card-actions v-if="messages.last_page > 1" class="justify-center">
                        <v-btn
                            variant="tonal"
                            :disabled="messages.current_page <= 1"
                            @click="goPage(messages.current_page - 1)"
                            icon="mdi-chevron-left"
                        />
                        <span class="mx-3 text-medium-emphasis">
              Pàgina {{ messages.current_page }} / {{ messages.last_page }}
            </span>
                        <v-btn
                            variant="tonal"
                            :disabled="messages.current_page >= messages.last_page"
                            @click="goPage(messages.current_page + 1)"
                            icon="mdi-chevron-right"
                        />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import axios from 'axios'

/** Estat base **/
const templates = ref([])
const selected = ref(null)
const search = ref('')

/** Refs per inserir variables al subjecte/cos */
const subjectRef = ref(null)
const bodyRef = ref(null)
const lastFocus = ref('body') // 'subject' | 'body'
const insertMode = ref('safe') // 'safe' | 'raw' | 'block'

/** Inserció al cursor */
const insertAtCursor = (el, text) => {
    if (!el) return
    if (typeof el.selectionStart === 'number') {
        const start = el.selectionStart, end = el.selectionEnd
        el.setRangeText(text, start, end, 'end')
        el.dispatchEvent(new Event('input', { bubbles: true }))
        el.focus()
    } else {
        el.value = (el.value || '') + text
        el.dispatchEvent(new Event('input', { bubbles: true }))
        el.focus()
    }
}
const makeSnippet = (key) => {
    if (insertMode.value === 'raw')   return `{{{ ${key} }}}`
    if (insertMode.value === 'block') return `{{#if ${key}}}\n  {{ ${key} }}\n{{/if}}`
    return `{{ ${key} }}` // safe per defecte
}
const insertVar = async (key, target = 'auto') => {
    const snippet = makeSnippet(key)
    await nextTick()
    let el = null
    if (target === 'subject') {
        el = subjectRef.value?.$el?.querySelector('input')
    } else if (target === 'body') {
        el = bodyRef.value?.$el?.querySelector('textarea')
    } else {
        el = (lastFocus.value === 'subject')
            ? subjectRef.value?.$el?.querySelector('input')
            : bodyRef.value?.$el?.querySelector('textarea')
    }
    insertAtCursor(el, snippet)
}

/** Missatges (historial) i proves */
const messages = ref({ data: [], current_page: 1, last_page: 1 })
const testTo = ref('')

/** Loading flags */
const loading = ref({ templates: false, messages: false, vars: false })
const saving = ref(false)
const sending = ref(false)

const tab = ref('template') // 'template' | 'variables'

/** Variables **/
const vars = ref([])
const savingVars = ref(false)
const typeOptions = ['string','number','boolean','date','array','json','url','email']

/** Previsualització **/
const payloadText = ref('')
const render = ref({ subject: '', body: '' })
const rendering = ref(false)

/** Helper auth **/
const authCfg = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    withCredentials: true
})

/** Columnes historial **/
const historyHeaders = [
    { title: 'Data', key: 'created_at', width: 160 },
    { title: 'Slug', key: 'template_slug', width: 160 },
    { title: 'Destinatari', key: 'to_email', width: 280 },
    { title: 'Subjecte', key: 'subject', width: 420 },
    { title: 'Estat', key: 'status', width: 120 },
    { title: 'Provider ID', key: 'provider_message_id' }
]

/** Utils **/
const formatDate = (str) => {
    if (!str) return '—'
    const d = new Date(str)
    const pad = (n) => (n < 10 ? '0' + n : n)
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const chipColor = (status) => {
    switch (status) {
        case 'sent': return 'success'
        case 'queued': return 'info'
        case 'failed': return 'error'
        case 'suppressed': return 'grey'
        default: return 'primary'
    }
}
const usesJsonEditor = (type) => ['array','json'].includes(type)

/** Carrega **/
const loadTemplates = async () => {
    loading.value.templates = true
    try {
        const { data } = await axios.get('/api/emails/templates', authCfg())
        templates.value = data || []
        if (!selected.value && templates.value.length) {
            selected.value = { ...templates.value[0] }
        }
    } finally {
        loading.value.templates = false
    }
}
const loadMessages = async (page = 1) => {
    loading.value.messages = true
    try {
        const { data } = await axios.get(`/api/emails?page=${page}`, authCfg())
        messages.value = data
    } finally {
        loading.value.messages = false
    }
}
const goPage = (p) => loadMessages(p)

const loadVars = async () => {
    if (!selected.value?.slug) return
    loading.value.vars = true
    try {
        const { data } = await axios.get(`/api/emails/templates/${selected.value.slug}/variables`, authCfg())
        vars.value = (data || []).map((x, idx) => ({
            key: x.key,
            label: x.label,
            type: x.type || 'string',
            required: !!x.required,
            rules: x.rules || '',
            default: usesJsonEditor(x.type) ? '' : (x.default ?? ''),
            defaultJson: usesJsonEditor(x.type) ? JSON.stringify(x.default ?? null) : '',
            description: x.description || '',
            sort: x.sort ?? idx
        })).sort((a,b)=>a.sort - b.sort)
    } finally {
        loading.value.vars = false
    }
}

/** Accions PLANTILLA **/
const newTemplate = () => {
    selected.value = {
        slug: '', name: '', subject: '', body_blade: '',
        locale: 'ca', is_active: true, description: ''
    }
    tab.value = 'template'
    vars.value = []
    payloadText.value = ''
    render.value = { subject: '', body: '' }
}
const selectTemplate = (tpl) => {
    selected.value = { ...tpl }
    // carrega les variables perquè estiguin disponibles per inserir
    loadVars()
    payloadText.value = ''
    render.value = { subject: '', body: '' }
}
const saveTemplate = async () => {
    if (!selected.value?.slug || !selected.value?.name) return
    saving.value = true
    try {
        await axios.post('/api/emails/templates', selected.value, authCfg())
        await loadTemplates()
        const match = templates.value.find(t => t.slug === selected.value.slug)
        if (match) selected.value = { ...match }
    } finally {
        saving.value = false
    }
}
const sendTest = async () => {
    if (!selected.value?.slug || !testTo.value) return
    sending.value = true
    try {
        await axios.post('/api/emails/send-test', {
            slug: selected.value.slug,
            to: testTo.value,
            data: { "user.name": "Prova", "loginUrl": location.origin + "/login" }
        }, authCfg())
        await loadMessages(1)
    } finally {
        sending.value = false
    }
}

/** Accions VARIABLES **/
const switchToVariables = async () => {
    tab.value = 'variables'
    await loadVars()
}
const addVarRow = () => {
    vars.value.push({
        key: '', label: '', type: 'string', required: false, rules: '',
        default: '', defaultJson: '', description: '', sort: vars.value.length
    })
}
const delVar = (i) => { vars.value.splice(i,1); reindexSort() }
const dupVar = (i) => {
    const c = JSON.parse(JSON.stringify(vars.value[i]))
    c.sort = vars.value.length
    vars.value.push(c)
}
const moveVar = (i, delta) => {
    const j = i + delta
    if (j < 0 || j >= vars.value.length) return
    const tmp = vars.value[i]
    vars.value[i] = vars.value[j]
    vars.value[j] = tmp
    reindexSort()
}
const reindexSort = () => { vars.value = vars.value.map((v,idx)=>({ ...v, sort: idx })) }

const saveVariables = async () => {
    if (!selected.value?.slug) return
    savingVars.value = true
    try {
        const items = vars.value.map(v => {
            let def = null
            if (usesJsonEditor(v.type)) {
                try { def = v.defaultJson ? JSON.parse(v.defaultJson) : null } catch { def = null }
            } else {
                def = v.default === '' ? null : v.default
            }
            return {
                key: v.key,
                label: v.label || null,
                type: v.type,
                required: !!v.required,
                rules: v.rules || null,
                default: def,
                description: v.description || null,
                sort: v.sort ?? 0
            }
        })
        await axios.post(`/api/emails/templates/${selected.value.slug}/variables`, { items }, authCfg())
        await loadVars()
    } finally {
        savingVars.value = false
    }
}

/** JSON helpers i preview **/
const safeParse = (s) => { try { return JSON.parse(s) } catch { return null } }
const buildDefaultsDot = () => {
    const dot = {}
    for (const v of vars.value) {
        if (v.key && (v.default !== '' || v.defaultJson)) {
            const val = usesJsonEditor(v.type) ? (v.defaultJson ? safeParse(v.defaultJson) : null) : v.default
            if (val !== null && val !== '' && val !== undefined) dot[v.key] = val
        }
    }
    return dot
}
const fillJsonDefaults = () => { payloadText.value = JSON.stringify(buildDefaultsDot(), null, 2) }
const copyPayload = async () => {
    try { await navigator.clipboard.writeText(payloadText.value || '{}') } catch {}
}
const doRender = async () => {
    if (!selected.value?.slug) return
    rendering.value = true
    try {
        const dataPayload = payloadText.value && payloadText.value.trim() ? payloadText.value : '{}'
        const { data } = await axios.post('/api/emails/render', { slug: selected.value.slug, data: dataPayload }, authCfg())
        render.value = data || { subject: '', body: '' }
    } finally {
        rendering.value = false
    }
}

/** Derivades i watchers **/
const filteredTemplates = computed(() => {
    const q = search.value.trim().toLowerCase()
    if (!q) return [...templates.value].sort((a,b)=>a.slug.localeCompare(b.slug))
    return templates.value
        .filter(t => (t.slug + ' ' + (t.name||'')).toLowerCase().includes(q))
        .sort((a,b)=>a.slug.localeCompare(b.slug))
})
watch(() => selected.value?.slug, () => {
    payloadText.value = ''
    render.value = { subject: '', body: '' }
    // si canvia de plantilla, recarrega variables perquè la barra d’inserció mostri les noves
    if (selected.value?.slug) loadVars()
})

/** Mount **/
onMounted(async () => {
    await Promise.all([loadTemplates(), loadMessages(1)])
    if (selected.value?.slug) await loadVars()
})
</script>
